import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BellRing, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useToast } from "@/hooks/use-toast";
import {
  buildPaymentReminderRecipients,
  type PaymentReminderRecipient,
} from "@/lib/payments/bulkPaymentReminders";
import { notificationStore } from "@/store/api/notificationStore";
import type { CoverageInfo } from "@/types/payments";
import type { TransactionData } from "@/types/types";

interface BulkPaymentReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactions: TransactionData[];
  coverageBySub: Record<string, CoverageInfo>;
}

export function BulkPaymentReminderDialog({
  open,
  onOpenChange,
  transactions,
  coverageBySub,
}: BulkPaymentReminderDialogProps) {
  const { t } = useTranslation(["payments", "common"]);
  const { formatCurrency } = useCurrency();
  const { toast } = useToast();
  const recipients = useMemo(
    () => buildPaymentReminderRecipients(transactions, coverageBySub),
    [transactions, coverageBySub]
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set(recipients.map((recipient) => recipient.userId)));
    setMessage(t("bulkRemind.defaultMessage"));
  }, [open, recipients, t]);

  const allSelected = recipients.length > 0 && selectedIds.size === recipients.length;
  const toggleRecipient = (recipient: PaymentReminderRecipient, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(recipient.userId);
      else next.delete(recipient.userId);
      return next;
    });
  };

  const onSend = async () => {
    if (!message.trim() || selectedIds.size === 0 || sending) return;
    setSending(true);
    try {
      const result = await notificationStore.createPaymentReminders([...selectedIds], {
        title: t("remind.notificationTitle"),
        text: message.trim(),
      });
      if (!result.ok) {
        toast({
          title: t("common:error"),
          description: t("bulkRemind.errorDesc"),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: t("bulkRemind.successTitle"),
        description: t("bulkRemind.successDesc", {
          count: result.inserted,
          ignored: result.ignored,
        }),
      });
      onOpenChange(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={sending ? undefined : onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden sm:max-h-[90dvh] sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5" />
            {t("bulkRemind.title")}
          </DialogTitle>
          <DialogDescription>{t("bulkRemind.description")}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
          <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
            <Checkbox
              checked={allSelected ? true : selectedIds.size > 0 ? "indeterminate" : false}
              onCheckedChange={(checked) =>
                setSelectedIds(checked === true ? new Set(recipients.map((r) => r.userId)) : new Set())
              }
              disabled={sending || recipients.length === 0}
              aria-label={t("bulkRemind.selectAll")}
            />
            {allSelected ? t("bulkRemind.deselectAll") : t("bulkRemind.selectAll")}
          </label>
          <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
            {t("bulkRemind.selectedCount", { count: selectedIds.size })}
          </span>
        </div>

        <div
          className="min-h-[8rem] flex-1 touch-pan-y overflow-y-auto overscroll-contain rounded-md border sm:max-h-[34dvh]"
          role="group"
          aria-label={t("bulkRemind.title")}
          tabIndex={0}
        >
          <div className="divide-y">
            {recipients.map((recipient) => (
              <label
                key={recipient.userId}
                className="flex cursor-pointer items-start gap-3 p-3 hover:bg-muted/40"
              >
                <Checkbox
                  checked={selectedIds.has(recipient.userId)}
                  onCheckedChange={(checked) => toggleRecipient(recipient, checked === true)}
                  disabled={sending}
                  aria-label={t("bulkRemind.selectClient", { client: recipient.client })}
                  className="mt-1"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{recipient.client}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {recipient.plans.join(", ") || t("bulkRemind.membership")}
                  </span>
                </span>
                <span className="shrink-0 text-sm font-semibold tabular-nums">
                  {formatCurrency(recipient.amountLeft)}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="bulk-payment-reminder-message" className="text-sm font-medium">
            {t("bulkRemind.messageLabel")}
          </label>
          <Textarea
            id="bulk-payment-reminder-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className="min-h-[100px] text-base md:text-sm"
            maxLength={1000}
            disabled={sending}
          />
          <p className="text-xs text-muted-foreground">{t("bulkRemind.placeholdersHelp")}</p>
        </div>

        <DialogFooter className="flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={sending}>
            {t("common:cancel")}
          </Button>
          <Button className="flex-1" onClick={onSend} disabled={sending || selectedIds.size === 0 || !message.trim()}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {t("bulkRemind.send", { count: selectedIds.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}